import type { Locale, StudentProfileContract } from "@chams-pay/contracts";
import {
  documentMessages,
  operationsMessages,
  paymentMessages,
  translate,
} from "@chams-pay/contracts";
import { FinancialDocument, printFinancialDocument } from "@chams-pay/document-ui";
import {
  Block,
  BlockTitle,
  Button,
  Navbar,
  NavbarBackLink,
  Page,
  Preloader,
  Segmented,
  SegmentedButton,
} from "konsta/react";
import { Printer } from "lucide-react";
import { periodLabel } from "../core/format.js";
import type { PaymentController } from "../core/use-payment.js";
import { remainingAfterAllocation, useCountUp } from "../core/use-payment.js";
import { usePreparedWhatsAppIntent } from "../core/use-whatsapp-intent.js";
import { DatePicker } from "../ui/date-picker.js";
import { Amount, Bidi, Count, DateValue } from "../ui/figures.js";
import { MobileA4DocumentPreview } from "./a4-document-preview.js";
import { showMobileFeedback } from "./app-feedback.js";
import { MobileDraggableSheet, MobileDrawerHandle } from "./draggable-sheet.js";
import { PeriodGrid } from "../desktop/surfaces.js";
import { MobileWhatsAppActions } from "./whatsapp-actions.js";

const stepKeys = ["sp.stepA", "sp.stepB", "sp.stepC", "sp.stepD", "sp.stepE"] as const;

function PaymentField({
  label,
  value,
  info,
  inputMode,
  maxLength,
  figure = false,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly info?: string;
  readonly inputMode?: "text" | "numeric";
  readonly maxLength?: number;
  readonly figure?: boolean;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-heading text-[11px] font-semibold text-muted-foreground">{label}</span>
      <input
        type="text"
        inputMode={inputMode}
        maxLength={maxLength}
        value={value}
        className={`min-h-14 w-full rounded-2xl border border-input bg-white px-4 font-heading text-sm font-semibold text-foreground outline-none transition-[border-color,box-shadow] duration-fast placeholder:text-muted-2 focus:border-primary focus:ring-3 focus:ring-primary/10 ${
          figure ? "chams-figure text-end" : ""
        }`}
        onChange={(event) => onChange(event.target.value)}
      />
      {info && (
        <small className="font-heading text-[10.5px] leading-relaxed text-muted-foreground">
          {info}
        </small>
      )}
    </label>
  );
}

function Notice({
  tone,
  icon,
  title,
  className,
  children,
}: {
  readonly tone: "info" | "warning" | "success" | "error";
  readonly icon?: React.ReactNode;
  readonly title?: string;
  readonly className?: string;
  readonly children: React.ReactNode;
}) {
  const tones = {
    info: "bg-info-soft text-info",
    warning: "bg-warning-soft text-warning",
    success: "bg-success-soft text-success",
    error: "bg-destructive-soft text-destructive",
  } as const;
  return (
    <Block
      className={`!my-3 flex items-start gap-2 rounded-2xl px-4 py-3 text-sm ${tones[tone]} ${className ?? ""}`}
    >
      {icon}
      <span className="flex flex-col gap-1">
        {title && <strong className="font-heading text-[13px]">{title}</strong>}
        <span className="text-xs leading-relaxed">{children}</span>
      </span>
    </Block>
  );
}

export function MobilePaymentPopup({
  locale,
  online,
  csrf,
  student,
  payment,
  yearContext,
  synchronize,
  opened,
  onClose,
}: {
  readonly locale: Locale;
  readonly online: boolean;
  readonly csrf: string;
  readonly student: StudentProfileContract;
  readonly payment: PaymentController;
  readonly yearContext?: { readonly role: "historical" | "next"; readonly academicYear: string };
  readonly synchronize: () => Promise<void>;
  readonly opened: boolean;
  readonly onClose: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const text = paymentMessages(locale);
  const operationsText = operationsMessages(locale);
  const documentText = documentMessages(payment.receiptLocale);
  const running = useCountUp(payment.allocatedNumeric);
  const paidPeriodLabels = payment.selectedPeriodEntries
    .filter((entry) =>
      entry.obligations.some((obligation) =>
        payment.allocations.some(
          (allocation) =>
            allocation.obligationId === obligation.id && /^[1-9][0-9]*$/.test(allocation.amountMad),
        ),
      ),
    )
    .map((entry) => ({ period: entry.period, label: periodLabel(entry.period, locale) }));
  const cash = payment.draft.method === "cash";
  const queuedOffline =
    !online && (payment.result?.queueState === "queued" || payment.result?.queueState === "failed");
  const whatsapp = usePreparedWhatsAppIntent({
    paymentId: payment.result?.payment.id ?? null,
    locale: payment.receiptLocale,
    csrf,
    enabled: opened && payment.step === "document" && online,
    beforePrepare: synchronize,
  });

  const close = () => {
    onClose();
    payment.restart();
  };

  return (
    <MobileDraggableSheet
      opened={opened}
      onClose={close}
      withHandle={false}
      className="mobile-workspace-drawer mobile-payment-workspace w-full rounded-t-2xl"
      data-testid="new-payment-surface"
    >
      <Page
        className="chams-page mobile-workspace-drawer-page payment-flow flex flex-col bg-canvas"
        data-testid="payment-workflow"
      >
        <MobileDrawerHandle />
        <Navbar
          className="mobile-drawer-drag-zone sticky top-0 z-30"
          data-mobile-drawer-drag-zone=""
          title={t(
            yearContext?.role === "next"
              ? "sp.takeNextYearPayment"
              : yearContext?.role === "historical"
                ? "sp.takeHistoricalYearPayment"
                : "sp.takePayment",
          )}
          subtitle={`${locale === "ar" ? student.nameAr : student.nameFr} · ${payment.stepNumber}/5`}
          left={<NavbarBackLink component="button" text={text.close} onClick={close} />}
        />

        <div className="payment-steps flex items-center gap-1.5 px-4 pt-3" aria-hidden="true">
          {([1, 2, 3, 4, 5] as const).map((number) => (
            <span
              key={number}
              data-active={payment.stepNumber >= number}
              className="h-1.5 flex-1 rounded-full bg-border transition-colors duration-base data-[active=true]:bg-primary"
            />
          ))}
          <strong className="ms-2 font-heading text-2xs text-muted-foreground">
            {t(stepKeys[payment.stepNumber - 1] ?? "sp.stepA")}
          </strong>
        </div>

        {payment.step === "selection" && (
          <section className="payment-step animate-fade-up">
            <Block className="payment-step__heading !my-3 text-xs leading-relaxed text-muted-foreground">
              {t("sp.step1Body")}
            </Block>
            <div className="px-4">
              <PeriodGrid
                locale={locale}
                entries={payment.periods}
                selected={payment.selectedPeriods}
                compact
                onToggle={payment.togglePeriod}
                label={t("sp.stepA")}
              />
            </div>
          </section>
        )}

        {payment.step === "allocation" && (
          <section className="payment-step animate-fade-up">
            <Block className="payment-step__heading !my-3 text-xs leading-relaxed text-muted-foreground">
              {t("sp.step2Body")}
            </Block>
            <div className="allocation-list mx-4 flex flex-col gap-4 rounded-2xl bg-white p-4 shadow-e1">
              {payment.selectedPeriodEntries.flatMap((entry) =>
                entry.obligations.map((obligation) => {
                  const allocation = payment.allocations.find(
                    (item) => item.obligationId === obligation.id,
                  );
                  const label = locale === "ar" ? obligation.labelAr : obligation.labelFr;
                  const remaining = remainingAfterAllocation(
                    obligation.remainingMad,
                    allocation?.amountMad ?? "0",
                  );
                  return (
                    <PaymentField
                      key={obligation.id}
                      label={`${text.allocated} ${label}`}
                      inputMode="numeric"
                      value={allocation?.amountMad ?? "0"}
                      figure
                      info={
                        remaining === null
                          ? t("sp.invalid")
                          : `${t("sp.remaining")} ${remaining} · ${t("sp.total")} ${obligation.remainingMad} · ${periodLabel(entry.period, locale)}`
                      }
                      onChange={(value) => payment.setAllocation(obligation.id, value)}
                    />
                  );
                }),
              )}
            </div>
            <Block
              strong
              inset
              className="allocation-total flex items-center justify-between gap-2"
              data-valid={payment.allocationsValid}
            >
              <span className="text-sm text-muted-foreground">{t("sp.total")}</span>
              <strong className="font-heading text-primary">
                <Amount value={payment.allocatedNumeric} locale={locale} />
              </strong>
              <small
                className={`rounded-full px-2 py-1 text-2xs font-bold ${
                  payment.allocationsValid
                    ? "bg-success-soft text-success"
                    : "bg-destructive-soft text-destructive"
                }`}
              >
                {t(payment.allocationsValid ? "sp.valid" : "sp.invalid")}
              </small>
            </Block>
          </section>
        )}

        {payment.step === "details" && (
          <section className="payment-step animate-fade-up">
            <div className="mx-4 my-4 flex flex-col gap-2.5">
              <h2 className="font-heading text-[11px] font-semibold text-muted-foreground">
                {t("sp.method")}
              </h2>
              <Segmented
                strong
                className="mobile-segmented method-picker w-full"
                role="radiogroup"
                data-tab-count="3"
                data-active-index={
                  payment.draft.method === "cash"
                    ? "0"
                    : payment.draft.method === "cheque"
                      ? "1"
                      : "2"
                }
              >
                {(["cash", "cheque", "transfer"] as const).map((value) => (
                  <SegmentedButton
                    key={value}
                    role="radio"
                    aria-checked={payment.draft.method === value}
                    active={payment.draft.method === value}
                    className="min-h-12"
                    onClick={() => payment.patchDraft({ method: value })}
                  >
                    {text[value]}
                  </SegmentedButton>
                ))}
              </Segmented>
            </div>

            <div className="payment-fields mx-4 flex flex-col gap-4 rounded-2xl bg-white p-4 shadow-e1">
              {payment.draft.method === "cheque" && (
                <>
                  <PaymentField
                    label={text.chequeNumber}
                    value={payment.draft.chequeNumber}
                    figure
                    onChange={(value) => payment.patchDraft({ chequeNumber: value })}
                  />
                  <PaymentField
                    label={text.bank}
                    value={payment.draft.bank}
                    onChange={(value) => payment.patchDraft({ bank: value })}
                  />
                  <PaymentField
                    label={text.drawer}
                    value={payment.draft.drawer}
                    onChange={(value) => payment.patchDraft({ drawer: value })}
                  />
                </>
              )}
              {payment.draft.method === "transfer" && (
                <>
                  <PaymentField
                    label={text.sender}
                    value={payment.draft.senderLabel}
                    onChange={(value) => payment.patchDraft({ senderLabel: value })}
                  />
                  <PaymentField
                    label={text.bank}
                    value={payment.draft.bank}
                    onChange={(value) => payment.patchDraft({ bank: value })}
                  />
                  <PaymentField
                    label={text.transferReference}
                    value={payment.draft.transferReference}
                    figure
                    onChange={(value) => payment.patchDraft({ transferReference: value })}
                  />
                  <DatePicker
                    locale={locale}
                    label={text.statedDate}
                    showLabel
                    className="payment-polished-date"
                    max={payment.today}
                    value={payment.draft.statedDate}
                    onChange={(value) => payment.patchDraft({ statedDate: value })}
                  />
                </>
              )}
              <DatePicker
                locale={locale}
                label={t("sp.receivedDate")}
                showLabel
                className="payment-polished-date"
                min={student.calendar.activeFrom}
                max={payment.today}
                value={payment.draft.receivedDate}
                onChange={(value) => payment.patchDraft({ receivedDate: value })}
              />
              <PaymentField
                label={`${t("sp.note")} — ${t("sp.optional")}`}
                maxLength={500}
                value={payment.draft.note}
                onChange={(value) => payment.patchDraft({ note: value })}
              />
            </div>

            {payment.backdated && (
              <Block strong inset className="!bg-warning-soft animate-fade-up flex flex-col gap-2">
                <strong className="font-heading text-xs text-warning">
                  {t("sp.backdatedTitle")}
                </strong>
                <input
                  type="text"
                  aria-label={t("sp.backdatedTitle")}
                  placeholder={t("sp.backdatedPlaceholder")}
                  value={payment.draft.backdatingReason}
                  onChange={(event) => payment.patchDraft({ backdatingReason: event.target.value })}
                  className="min-h-14 rounded-2xl border border-warning-border bg-white px-4 font-heading text-sm font-semibold outline-none transition-[border-color,box-shadow] duration-fast focus:border-warning focus:ring-3 focus:ring-warning/10"
                />
              </Block>
            )}
          </section>
        )}

        {payment.step === "confirmation" && (
          <section className="payment-step confirmation-step animate-fade-up">
            <BlockTitle>{t("sp.confirmTitle")}</BlockTitle>
            <Block strong inset>
              <dl className="flex flex-col gap-2.5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-muted-foreground">{t("sp.student")}</dt>
                  <dd className="font-bold">{locale === "ar" ? student.nameAr : student.nameFr}</dd>
                </div>
                <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-3">
                  <dt className="whitespace-nowrap text-muted-foreground">
                    {t("sp.obligationsPaid")}
                  </dt>
                  <dd className="financial-document__period-pills min-w-0 font-bold">
                    {paidPeriodLabels.map((entry) => (
                      <span className="financial-document__period-pill" key={entry.period}>
                        {entry.label}
                      </span>
                    ))}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-muted-foreground">{t("sp.method")}</dt>
                  <dd className="font-bold">{text[payment.draft.method]}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-muted-foreground">{t("sp.receivedDate")}</dt>
                  <dd className="font-bold">
                    <DateValue value={payment.draft.receivedDate} />
                  </dd>
                </div>
                <span aria-hidden="true" className="h-px bg-border" />
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="font-heading font-bold">{t("sp.total")}</dt>
                  <dd className="font-heading text-2xl text-primary">
                    <Amount value={payment.allocatedNumeric} locale={locale} />
                  </dd>
                </div>
              </dl>
            </Block>
            <Notice tone="error" className="!mx-4">
              {t("sp.immutableWarn")}
            </Notice>
            {payment.error && (
              <Block
                role="alert"
                className="!my-3 animate-shake rounded-2xl bg-destructive-soft px-4 py-3 text-sm text-destructive"
              >
                {text[payment.error]}
              </Block>
            )}
          </section>
        )}

        {payment.step === "document" && payment.result && (
          <section className="payment-result animate-fade-up pb-8">
            <Notice
              tone="success"
              title={t(cash ? "sp.receiptIssued" : "sp.provIssued")}
              className="!mx-4"
            >
              {t(cash ? "sp.receiptIssuedSub" : "sp.provIssuedSub")}{" "}
              <Bidi>{payment.result.payment.reference}</Bidi>
            </Notice>
            {!cash && (
              <Notice tone="warning" title={t("sp.provTitle")} className="!mx-4">
                {t("sp.provBody")}
              </Notice>
            )}
            {queuedOffline && (
              <Notice tone="info" title={t("sp.queuedTitle")} className="!mx-4">
                {t("sp.queuedBody")}
              </Notice>
            )}
            <Block className="!my-2">
              <Segmented
                strong
                className="mobile-segmented receipt-language no-print"
                data-tab-count="2"
                data-active-index={payment.receiptLocale === "ar" ? "0" : "1"}
              >
                <SegmentedButton
                  active={payment.receiptLocale === "ar"}
                  className="min-h-11"
                  onClick={() => payment.setReceiptLocale("ar")}
                >
                  {documentText.arabic}
                </SegmentedButton>
                <SegmentedButton
                  active={payment.receiptLocale === "fr"}
                  className="min-h-11"
                  onClick={() => payment.setReceiptLocale("fr")}
                >
                  {documentText.french}
                </SegmentedButton>
              </Segmented>
            </Block>
            <Block className="payment-result__actions no-print flex flex-col gap-2">
              <Button
                component="button"
                type="button"
                large
                rounded
                className="min-h-12 gap-2"
                onClick={printFinancialDocument}
              >
                <Printer aria-hidden="true" className="size-4" />
                {t("sp.print")}
              </Button>
              <MobileWhatsAppActions
                whatsapp={whatsapp}
                canPrepare={online && Boolean(csrf)}
                text={{
                  send: operationsText.whatsapp,
                  generate: operationsText.whatsappGenerate,
                  preparing: operationsText.whatsappPreparing,
                  copy: operationsText.whatsappCopy,
                  copied: operationsText.whatsappCopied,
                  prepareFailed: operationsText.whatsappPrepareFailed,
                  retry: operationsText.whatsappRetry,
                  copyFailed: operationsText.whatsappCopyFailed,
                }}
                onCopied={() => showMobileFeedback("copied")}
              />
            </Block>
            <Block className="payment-result__document animate-rise">
              <MobileA4DocumentPreview locale={payment.receiptLocale}>
                <FinancialDocument
                  locale={payment.receiptLocale}
                  snapshot={payment.result.document}
                />
              </MobileA4DocumentPreview>
            </Block>
          </section>
        )}

        <div className="mobile-drawer-footer flex-col gap-2.5">
          <div className="flex w-full items-center gap-3">
            {payment.stepNumber > 1 && payment.step !== "document" && (
              <Button
                component="button"
                type="button"
                rounded
                outline
                className="back-button min-h-12 flex-1 px-4"
                onClick={payment.retreat}
              >
                {t("sp.back")}
              </Button>
            )}
            {payment.step === "document" ? (
              <Button
                component="button"
                type="button"
                large
                rounded
                className="min-h-12 flex-1 px-6"
                onClick={close}
              >
                {t("sp.finish")}
              </Button>
            ) : payment.step === "confirmation" ? (
              <Button
                component="button"
                type="button"
                large
                rounded
                className="min-h-12 flex-1 gap-2 px-6"
                disabled={!payment.canAdvance}
                onClick={() => void payment.finalize()}
              >
                {payment.busy && <Preloader size="w-5 h-5" />}
                {payment.busy ? text.writing : t("sp.confirmPay")}
              </Button>
            ) : (
              <Button
                component="button"
                type="button"
                large
                rounded
                className="min-h-12 flex-1 px-6"
                disabled={!payment.canAdvance}
                onClick={payment.advance}
              >
                {t("sp.next")}
              </Button>
            )}
          </div>
          {payment.stepNumber < 4 && (
            <span className="flex items-baseline justify-center gap-2 text-center">
              <small className="text-2xs text-muted-foreground">{t("sp.runningTotal")}</small>
              <strong className="font-heading text-lg leading-none">
                <Amount value={running} locale={locale} />
              </strong>
            </span>
          )}
          {yearContext && (
            <Notice
              tone="warning"
              className="!m-0 w-full justify-center !rounded-xl border border-warning-border !px-3 !py-2 text-center"
            >
              <strong className="font-heading text-2xs">
                {t(
                  yearContext.role === "next"
                    ? "sp.nextYearPaymentNotice"
                    : "sp.historicalYearPaymentNotice",
                )}{" "}
                <Bidi>{yearContext.academicYear}</Bidi>
              </strong>
            </Notice>
          )}
          {!yearContext && (
            <Notice
              tone="info"
              className="!m-0 w-full justify-center !rounded-xl border border-teal-100 !bg-teal-50 !px-3 !py-2 text-center !text-primary-strong"
            >
              <strong className="font-heading text-2xs">
                {t("sp.currentYearPaymentNotice")} <Bidi>{student.calendar.academicYear}</Bidi>
              </strong>
            </Notice>
          )}
        </div>
      </Page>
    </MobileDraggableSheet>
  );
}
